import { original } from "@reduxjs/toolkit"
import { FormProvider, useForm } from "react-hook-form"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react"
import { CommentsSection } from "../../CommentsSection"
import { DEFAULT_COMMENTS_SORT } from "../../CommentsSection/helpers/commentsSortQuery"
import { findCommentInTree } from "../../CommentsSection/helpers/findCommentInTree"
import { resolveThreadAnchorId } from "../../CommentsSection/helpers/resolveThreadAnchorL2"
import { HIGHLIGHT_HOLD_MS } from "../../CommentsSection/helpers/commentHighlightSx"
import {
    mergeChildrenIntoParent,
    mergeNewCommentIntoCache,
    patchCommentTextInTree,
    removeCommentFromTree,
    sortCommentsByPublishedAtAsc,
} from "../../CommentsSection/helpers/mergeCommentBranch"
import { CommentDeleteConfirmModalBody } from "../../Modals/CommentDeleteConfirmModalBody"
import { CommentThreadModalBody, type SubmitCommentDetailedResult } from "../../Modals/CommentThreadModalBody"
import modalEmitter from "../../ModalComponent/modalEmitter"
import type { OpenL2ReplyThreadPayload } from "../../CommentsSection/ChildrenComponents/ReplyItem"
import { CommentsType } from "../../../types/common"
import { profileApi } from "../../../api/profile"
import {
    socialOrdersApi,
    useAddCommentToSocialOrderMutation,
    useDeleteSocialOrderCommentMutation,
    useGetSocialOrderCommentsQuery,
    useLazyGetSocialOrderCommentsQuery,
    useUpdateSocialOrderCommentMutation,
} from "../../../api/socialOrders"
import type { AppDispatch } from "../../../store/store"
import type {
    CommentsSortUi,
    IProjectCommentCountersByType,
    ProjectCommentTabCounts,
} from "../../../api/types/projects"
import type { ISocialOrderResource } from "../../../api/types/socialOrders"
import { CURSOR_LIST_PAGE_SIZE } from "../../../consts/api"

type CommentsWrapperProps = {
    socialOrderId: number
    cacheKey: number | string
    socialOrder?: ISocialOrderResource | null
    onClose?: () => void
    initialCommentTab?: CommentsType
    notificationParentCommentId?: number
    notificationCommentId?: number
    onNotificationDeepLinkConsumed?: () => void
    commentsSectionRootRef?: RefObject<HTMLElement | null>
}

type CommentsFormValues = {
    commentText: string
    replyText: string
    filterType: CommentsType
    sort: CommentsSortUi
    mobileReplyTarget: { id: number; name: string } | null
}

function resolveSocialOrderCommentTabCounts(
    comments: ISocialOrderResource["counters"]["comments"] | undefined | null
): ProjectCommentTabCounts {
    if (comments && typeof comments === "object") {
        return {
            opinions: Number(comments.opinions) || 0,
            suggestions: Number(comments.suggestions) || 0,
            questions: Number(comments.questions) || 0,
        }
    }
    return { opinions: 0, suggestions: 0, questions: 0 }
}

function incrementSocialOrderCountersForCreatedComment(
    commentsField: ISocialOrderResource["counters"]["comments"],
    createdType: string | undefined
): ISocialOrderResource["counters"]["comments"] {
    if (typeof commentsField === "number") {
        return commentsField + 1
    }
    if (!commentsField || typeof commentsField !== "object") return commentsField
    const c = commentsField as IProjectCommentCountersByType
    c.total = (Number(c.total) || 0) + 1
    const t = (createdType ?? "").toLowerCase()
    if (t === "opinion") c.opinions = (Number(c.opinions) || 0) + 1
    else if (t === "suggestion") c.suggestions = (Number(c.suggestions) || 0) + 1
    else if (t === "question") c.questions = (Number(c.questions) || 0) + 1
    return c
}

function decrementSocialOrderCountersForDeletedComment(
    commentsField: ISocialOrderResource["counters"]["comments"],
    deletedType: string | undefined
): ISocialOrderResource["counters"]["comments"] {
    if (typeof commentsField === "number") {
        return Math.max(0, commentsField - 1)
    }
    if (!commentsField || typeof commentsField !== "object") return commentsField
    const c = commentsField as IProjectCommentCountersByType
    c.total = Math.max(0, (Number(c.total) || 0) - 1)
    const t = (deletedType ?? "").toLowerCase()
    if (t === "opinion" || t === "opinions") c.opinions = Math.max(0, (Number(c.opinions) || 0) - 1)
    else if (t === "suggestion" || t === "suggestions") c.suggestions = Math.max(0, (Number(c.suggestions) || 0) - 1)
    else if (t === "question" || t === "questions") c.questions = Math.max(0, (Number(c.questions) || 0) - 1)
    return c
}

export const CommentsWrapper = ({
    socialOrderId,
    cacheKey,
    socialOrder,
    onClose,
    initialCommentTab,
    notificationParentCommentId,
    notificationCommentId,
    onNotificationDeepLinkConsumed,
    commentsSectionRootRef,
}: CommentsWrapperProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const [fetchCommentBranch] = useLazyGetSocialOrderCommentsQuery()
    const [loadingParentId, setLoadingParentId] = useState<number | null>(null)
    const [highlightCommentId, setHighlightCommentId] = useState<number | null>(null)
    const notificationBranchLoadedRef = useRef<number | null>(null)
    const notificationScrollDoneRef = useRef(false)
    const methods = useForm<CommentsFormValues>({
        defaultValues: {
            commentText: "",
            replyText: "",
            sort: DEFAULT_COMMENTS_SORT,
            filterType: initialCommentTab ?? CommentsType.opinions,
            mobileReplyTarget: null as { id: number; name: string } | null,
        },
    })

    useEffect(() => {
        if (initialCommentTab) {
            methods.setValue("filterType", initialCommentTab)
        }
    }, [initialCommentTab, methods])

    useEffect(() => {
        notificationBranchLoadedRef.current = null
        notificationScrollDoneRef.current = false
    }, [notificationParentCommentId, notificationCommentId, socialOrderId])

    const currentFilter = methods.watch("filterType") as CommentsType
    const currentSort = methods.watch("sort") as CommentsSortUi
    const { data: profile } = profileApi.useGetProfileQuery()
    const currentUserId = profile?.id ?? null
    const [addCommentToSocialOrder, { isLoading: isSubmittingComment }] = useAddCommentToSocialOrderMutation()
    const [updateComment, { isLoading: isUpdatingComment }] = useUpdateSocialOrderCommentMutation()
    const [deleteCommentMutation] = useDeleteSocialOrderCommentMutation()
    const canComment = socialOrder?.viewer?.permissions?.can_comment !== false
    const tabCounts = useMemo(
        () => resolveSocialOrderCommentTabCounts(socialOrder?.counters?.comments),
        [socialOrder?.counters?.comments],
    )

    const commentsListCacheKey = useMemo(
        () => ({
            socialOrderId,
            type: currentFilter,
            limit: CURSOR_LIST_PAGE_SIZE,
            sort: currentSort,
        }),
        [socialOrderId, currentFilter, currentSort],
    )

    const { data: commentsData, isLoading: isCommentsLoading, isError: isCommentsError } =
        useGetSocialOrderCommentsQuery(commentsListCacheKey, { skip: !socialOrderId })

    const submitCommentDetailed = useCallback(
        async ({ text, parentId }: { text: string; parentId?: number }): Promise<SubmitCommentDetailedResult> => {
            const trimmed = text.trim()
            if (!trimmed || !socialOrderId) return { ok: false }
            const body = parentId ? { text: trimmed, parent_id: parentId } : { text: trimmed }
            try {
                const created = await addCommentToSocialOrder({
                    socialOrderId,
                    type: currentFilter,
                    body,
                }).unwrap()

                dispatch(
                    socialOrdersApi.util.updateQueryData(
                        "getSocialOrderComments",
                        commentsListCacheKey,
                        (draft) =>
                            mergeNewCommentIntoCache(
                                original(draft) ?? [...draft],
                                created,
                                created.parent_id ?? null,
                                currentSort,
                            )
                    )
                )

                dispatch(
                    socialOrdersApi.util.updateQueryData("getSocialOrder", { id: cacheKey }, (draft) => {
                        if (!draft) return
                        draft.counters.comments = incrementSocialOrderCountersForCreatedComment(
                            draft.counters.comments,
                            created.type,
                        )
                    })
                )

                if (parentId) {
                    methods.setValue("replyText", "")
                    methods.setValue("mobileReplyTarget", null)
                    methods.setValue("commentText", "")
                } else {
                    methods.setValue("commentText", "")
                }
                return { ok: true, created }
            } catch {
                toast.error("Не удалось отправить комментарий")
                return { ok: false }
            }
        },
        [
            socialOrderId,
            currentFilter,
            currentSort,
            addCommentToSocialOrder,
            dispatch,
            methods,
            commentsListCacheKey,
            cacheKey,
        ],
    )

    const onSubmitComment = async ({ text, parentId }: { text: string; parentId?: number }): Promise<boolean> => {
        const result = await submitCommentDetailed({ text, parentId })
        return result.ok
    }

    const onUpdateComment = useCallback(
        async (commentId: number, text: string): Promise<boolean> => {
            try {
                const updated = await updateComment({ commentId, body: { text } }).unwrap()
                dispatch(
                    socialOrdersApi.util.updateQueryData(
                        "getSocialOrderComments",
                        commentsListCacheKey,
                        (draft) => patchCommentTextInTree(original(draft) ?? [...draft], commentId, updated)
                    )
                )
                return true
            } catch {
                toast.error("Не удалось сохранить изменения")
                return false
            }
        },
        [updateComment, dispatch, commentsListCacheKey],
    )

    const onDeleteComment = useCallback(
        (commentId: number): Promise<boolean> =>
            new Promise((resolve) => {
                let settled = false
                const settle = (value: boolean) => {
                    if (settled) return
                    settled = true
                    resolve(value)
                }

                const performDelete = async (): Promise<boolean> => {
                    try {
                        await deleteCommentMutation({ commentId }).unwrap()
                        dispatch(
                            socialOrdersApi.util.updateQueryData(
                                "getSocialOrderComments",
                                commentsListCacheKey,
                                (draft) => removeCommentFromTree(original(draft) ?? [...draft], commentId)
                            )
                        )
                        dispatch(
                            socialOrdersApi.util.updateQueryData("getSocialOrder", { id: cacheKey }, (draft) => {
                                if (!draft) return
                                draft.counters.comments = decrementSocialOrderCountersForDeletedComment(
                                    draft.counters.comments,
                                    currentFilter,
                                )
                            })
                        )
                        toast.success("Комментарий удалён")
                        settle(true)
                        return true
                    } catch {
                        toast.error("Не удалось удалить комментарий")
                        return false
                    }
                }

                modalEmitter.emitOpenModal({
                    dialog: {
                        title: "Удалить комментарий?",
                        body: (
                            <CommentDeleteConfirmModalBody
                                onConfirm={performDelete}
                                onCancel={() => settle(false)}
                            />
                        ),
                        modalToCentrPosition: true,
                        afterCloseCallBack: () => settle(false),
                    },
                    closeAtion: { visible: false },
                    confirmAtion: { visible: false },
                })
            }),
        [deleteCommentMutation, dispatch, commentsListCacheKey, cacheKey, currentFilter],
    )

    const handleOpenReplyThread = useCallback(
        (p: OpenL2ReplyThreadPayload) => {
            const initialComposerTarget =
                p.startReplyComposer === true
                    ? {
                          parentId: p.anchorCommentId,
                          firstName: p.anchorFirstName ?? "",
                          lastName: p.anchorLastName ?? "",
                      }
                    : undefined

            modalEmitter.emitOpenModal({
                dialog: {
                    title: "Ответы",
                    body: (
                        <CommentThreadModalBody
                            socialOrderId={socialOrderId}
                            commentType={currentFilter}
                            anchorL2Id={p.anchorCommentId}
                            excerpt={p.excerpt}
                            currentUserId={currentUserId}
                            submitCommentDetailed={submitCommentDetailed}
                            onUpdateComment={onUpdateComment}
                            isUpdatingComment={isUpdatingComment}
                            isSubmittingComment={isSubmittingComment}
                            initialComposerTarget={initialComposerTarget}
                            highlightCommentId={p.highlightCommentId}
                        />
                    ),
                    tallCentered: true,
                },
                closeAtion: { visible: false },
                confirmAtion: { visible: false },
            })
        },
        [
            socialOrderId,
            currentFilter,
            currentUserId,
            submitCommentDetailed,
            onUpdateComment,
            isUpdatingComment,
            isSubmittingComment,
        ],
    )

    const loadMoreReplies = useCallback(
        async (parentId: number) => {
            setLoadingParentId(parentId)
            try {
                const newNodes = await fetchCommentBranch({
                    socialOrderId,
                    type: currentFilter,
                    parentId,
                    limit: CURSOR_LIST_PAGE_SIZE,
                }).unwrap()
                const sortedNewNodes = sortCommentsByPublishedAtAsc(newNodes)

                dispatch(
                    socialOrdersApi.util.updateQueryData(
                        "getSocialOrderComments",
                        commentsListCacheKey,
                        (draft) =>
                            mergeChildrenIntoParent(
                                original(draft) ?? [...draft],
                                parentId,
                                sortedNewNodes
                            )
                    )
                )
            } catch {
                toast.error("Не удалось загрузить ответы")
            } finally {
                setLoadingParentId(null)
            }
        },
        [socialOrderId, currentFilter, dispatch, fetchCommentBranch, commentsListCacheKey],
    )

    useEffect(() => {
        if (!notificationParentCommentId) return
        if (notificationBranchLoadedRef.current === notificationParentCommentId) return
        if (isCommentsLoading || !commentsData) return

        const parentId = notificationParentCommentId
        if (loadingParentId === parentId) return

        const found = findCommentInTree(commentsData, parentId)
        const totalChildren = found?.counters?.children ?? 0
        const shownChildren = found?.children?.length ?? 0
        const needsLoad = !found || totalChildren > shownChildren

        if (needsLoad) {
            void loadMoreReplies(parentId).finally(() => {
                notificationBranchLoadedRef.current = parentId
            })
        } else {
            notificationBranchLoadedRef.current = parentId
        }
    }, [
        notificationParentCommentId,
        isCommentsLoading,
        commentsData,
        loadMoreReplies,
        loadingParentId,
    ])

    useEffect(() => {
        if (!notificationCommentId || !onNotificationDeepLinkConsumed) return
        if (notificationScrollDoneRef.current) return
        if (isCommentsLoading || !commentsData) return
        if (loadingParentId !== null) return

        if (notificationParentCommentId) {
            const parentBranchDone =
                notificationBranchLoadedRef.current === notificationParentCommentId &&
                loadingParentId === null
            if (!parentBranchDone) return
        }

        const targetId = notificationCommentId
        const el = commentsSectionRootRef?.current
            ? commentsSectionRootRef.current.querySelector<HTMLElement>(`#comment-${targetId}`)
            : document.getElementById(`comment-${targetId}`)

        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" })
            setHighlightCommentId(targetId)
            notificationScrollDoneRef.current = true
            onNotificationDeepLinkConsumed()
            return
        }

        const anchorId =
            (notificationParentCommentId != null
                ? resolveThreadAnchorId(commentsData, notificationParentCommentId)
                : null) ??
            resolveThreadAnchorId(commentsData, targetId) ??
            null

        if (anchorId != null) {
            const anchorNode = findCommentInTree(commentsData, anchorId)
            handleOpenReplyThread({
                anchorCommentId: anchorId,
                excerpt: anchorNode?.text ?? "",
                replyCount: anchorNode?.counters?.children ?? 0,
                highlightCommentId: targetId,
            })
            notificationScrollDoneRef.current = true
            onNotificationDeepLinkConsumed()
            return
        }

        toast.error("Комментарий не найден")
        notificationScrollDoneRef.current = true
        onNotificationDeepLinkConsumed()
    }, [
        notificationCommentId,
        notificationParentCommentId,
        onNotificationDeepLinkConsumed,
        isCommentsLoading,
        commentsData,
        loadingParentId,
        handleOpenReplyThread,
        commentsSectionRootRef,
    ])

    useEffect(() => {
        if (highlightCommentId == null) return
        const t = window.setTimeout(() => setHighlightCommentId(null), HIGHLIGHT_HOLD_MS)
        return () => window.clearTimeout(t)
    }, [highlightCommentId])

    return (
        <FormProvider {...methods}>
           <CommentsSection
               onClose={onClose}
               commentsData={commentsData}
               onSubmitComment={onSubmitComment}
               isSubmittingComment={isSubmittingComment}
               isCommentsLoading={isCommentsLoading}
               isCommentsError={isCommentsError}
               loadMoreReplies={loadMoreReplies}
               loadingParentId={loadingParentId}
               currentUserId={currentUserId}
               onUpdateComment={onUpdateComment}
               isUpdatingComment={isUpdatingComment}
               onDeleteComment={onDeleteComment}
               canComment={canComment}
               onOpenReplyThread={handleOpenReplyThread}
               commentForbiddenText="Вам запрещено комментировать этот социальный заказ."
               tabCounts={tabCounts}
               highlightCommentId={highlightCommentId}
               commentReactionCache={{ kind: "socialOrder", socialOrderId, type: currentFilter, sort: currentSort }}
           />
        </FormProvider>
    )
}
